import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  type FormProps,
  Input,
  Radio,
  Row,
  Select,
  Space,
} from 'antd';
import classNames from 'classnames';
import { useState } from 'react';

import IconDown from '@/assets/icons/down.svg?react';
import IconSearch from '@/assets/icons/search.svg?react';
import { FormItem, SectionTitle, SubmitButton } from '@/components';
import {
  FORM_ITEM_LAYOUT_COMMON,
  FORM_ITEM_LAYOUT_WITH_CHECKBOX,
} from '@/constants/form.constants';
import {
  CONTACT_NAME_PATTERN,
  EMAIL_PATTERN,
  NUMBER_PATTERN,
  PHONE_NUMBER_LENGTH_PATTERN,
  POSTAL_CODE_PATTERN,
} from '@/constants/regex.constants';
import { AccountResponseItem } from '@/features/account/types';
import {
  checkInvalidZipcode,
  handleChangeAddress,
  handleShowAddress,
} from '@/helpers/postal-code.helper';
import { transformEmojisIfHave } from '@/helpers/string.helper';
import { useDetectFormValuesChanged } from '@/hooks/use-detect-form-values-changed';
import { useUserInformation } from '@/hooks/use-user-information';

import { INVOICE_OR_SALE_FIGURE_TYPES_OPTIONS } from '../../constants';
import type { InvoiceAndSaleFigureType, InvoicesItem } from '../../types';

const { Option } = Select;

function filterInvoices(
  invoices: InvoicesItem[],
  isInvoiceTypeReportForEachStore?: boolean,
) {
  return invoices
    ?.filter(({ shop_id }) =>
      isInvoiceTypeReportForEachStore ? shop_id : !shop_id,
    )
    ?.map(({ acode, beneficiary_account: beneficiaryAccount, ...rest }) => ({
      ...rest,
      acode: acode ?? '',
      beneficiary_account: beneficiaryAccount ?? '',
    }));
}

export function BillingInformationForm({
  initialValues,
  ...rest
}: Readonly<FormProps>) {
  const [form] = Form.useForm();
  const { isSbsRoles } = useUserInformation();
  const [isInSelectViewMode, setIsInSelectViewMode] = useState(true);

  const invoiceType = Form.useWatch('invoice_type', form);
  const receiveSaleFigureEmailAccountIds = Form.useWatch(
    'receive_sale_figure_email_account_ids',
    form,
  );
  const isInvoiceTypeReportForEachStore =
    invoiceType === 'report-for-each-store';

  const {
    invoice_type,
    sale_figure_type,
    accounts,
    invoice_managements,
    invoice_registration,
    receive_sale_figure_email_account_ids,
  } = initialValues ?? {};

  const invoiceManagementsTypeStore = filterInvoices(invoice_managements, true);
  const invoiceManagementsTypeChain = filterInvoices(invoice_managements);

  const parsedInitialValues = {
    invoice_type,
    sale_figure_type,
    invoice_registration,
    invoice_managements:
      invoice_type === 'report-for-each-store'
        ? invoiceManagementsTypeStore
        : invoiceManagementsTypeChain,
    receive_sale_figure_email_account_ids,
  };

  const { handleFieldsChange } = useDetectFormValuesChanged({
    form,
    initialValues: parsedInitialValues,
  });

  const inputNameValidate = (
    invoice_type === 'report-for-each-store'
      ? invoiceManagementsTypeStore
      : invoiceManagementsTypeChain
  )?.map((_, index: number) => [
    ['invoice_managements', index, 'contact_name'],
    ['invoice_managements', index, 'company_name'],
    ['invoice_managements', index, 'zipcode'],
    ['invoice_managements', index, 'address'],
    ['invoice_managements', index, 'email_address'],
    ['invoice_managements', index, 'phone_number'],
  ]);
  const inputNameNeedReset = inputNameValidate
    ?.map((item, index: number) => [
      ...item,
      ['invoice_managements', index, 'acode'],
      ['invoice_managements', index, 'beneficiary_account'],
    ])
    .flat();

  const onValuesChange = (changedValues: {
    invoice_type: InvoiceAndSaleFigureType;
  }) => {
    const [changedKey, changedValue] = Object.entries(changedValues)[0];
    if (changedKey === 'invoice_type') {
      form.resetFields(inputNameNeedReset);
      if (changedValue === 'report-for-each-store') {
        form.setFieldValue('sale_figure_type', 'report-for-each-store');
        form.setFieldValue('invoice_managements', invoiceManagementsTypeStore);
      }
      if (changedValue === 'report-for-chain') {
        form.setFieldValue('invoice_managements', invoiceManagementsTypeChain);
      }
    }
  };

  return (
    <Form
      form={form}
      labelAlign="left"
      initialValues={parsedInitialValues}
      onFieldsChange={handleFieldsChange}
      onValuesChange={onValuesChange}
      {...rest}
    >
      <SectionTitle
        title="入力設定"
        {...(!isSbsRoles && {
          subTitle: '設定を変更する場合は営業担当までご連絡ください。',
        })}
      />
      <Card variant="borderless">
        <FormItem
          label="請求書発行単位"
          name="invoice_type"
          rules={[{ required: true }]}
          {...FORM_ITEM_LAYOUT_COMMON}
        >
          <Radio.Group disabled={!isSbsRoles}>
            <Space>
              {INVOICE_OR_SALE_FIGURE_TYPES_OPTIONS.map(({ label, value }) => (
                <Radio key={value} value={value}>
                  {label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </FormItem>
        <FormItem
          label="販売実績の入力単位"
          name="sale_figure_type"
          rules={[{ required: true }]}
          {...FORM_ITEM_LAYOUT_COMMON}
        >
          <Radio.Group
            disabled={isInvoiceTypeReportForEachStore || !isSbsRoles}
          >
            <Space>
              {INVOICE_OR_SALE_FIGURE_TYPES_OPTIONS.map(({ label, value }) => (
                <Radio key={value} value={value}>
                  {label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </FormItem>
      </Card>
      <SectionTitle title="担当者設定" className="mt-32" />
      <Card variant="borderless">
        <FormItem
          validateFirst
          name="receive_sale_figure_email_account_ids"
          rules={[{ required: true }]}
          label="実績申請の担当者"
          {...FORM_ITEM_LAYOUT_WITH_CHECKBOX}
        >
          <Select
            mode="multiple"
            suffixIcon={isInSelectViewMode ? <IconDown /> : <IconSearch />}
            onFocus={() => setIsInSelectViewMode(false)}
            onBlur={() => setIsInSelectViewMode(true)}
            {...(receiveSaleFigureEmailAccountIds?.length === 1 && {
              // eslint-disable-next-line unicorn/no-null
              removeIcon: null,
            })}
            showSearch={false}
            onInputKeyDown={event => event.stopPropagation()}
          >
            {accounts?.map((item: AccountResponseItem) => (
              <Option
                key={item.id}
                value={item.id}
                disabled={
                  receiveSaleFigureEmailAccountIds?.length === 1 &&
                  receiveSaleFigureEmailAccountIds.includes(item.id)
                }
              >
                {item.name}
              </Option>
            ))}
          </Select>
        </FormItem>
      </Card>
      {!!(
        isInvoiceTypeReportForEachStore
          ? invoiceManagementsTypeStore
          : invoiceManagementsTypeChain
      )?.length && (
        <>
          <SectionTitle
            title="請求書情報"
            {...(!isSbsRoles && {
              subTitle: '情報を変更する場合は営業担当までご連絡ください。',
            })}
            className="mt-32"
          />
          <Form.List name="invoice_managements">
            {fields =>
              fields?.map((field, index) => (
                <Card
                  key={field.key}
                  variant="borderless"
                  className={classNames({ 'mt-16': index })}
                  title={
                    isInvoiceTypeReportForEachStore
                      ? invoiceManagementsTypeStore?.[field.name]?.shop_name
                      : undefined
                  }
                >
                  <FormItem
                    validateFirst
                    label="会社名"
                    name={[field.name, 'company_name']}
                    rules={[
                      { required: true, whitespace: true },
                      {
                        max: 512,
                        transform(value) {
                          return transformEmojisIfHave(value);
                        },
                      },
                    ]}
                    {...FORM_ITEM_LAYOUT_COMMON}
                  >
                    <Input disabled={!isSbsRoles} />
                  </FormItem>
                  <FormItem
                    label="住所"
                    required
                    htmlFor="zipcode"
                    {...FORM_ITEM_LAYOUT_COMMON}
                    shouldUpdate
                  >
                    {({ getFieldValue }) => (
                      <>
                        <Row>
                          <Col span={24}>
                            <FormItem
                              validateFirst
                              isHiddenDivider
                              name={[field.name, 'zipcode']}
                              messageVariables={{ label: '郵便番号' }}
                              className="mb-8"
                              rules={[
                                { required: true, whitespace: true },
                                {
                                  pattern: POSTAL_CODE_PATTERN,
                                  message:
                                    '郵便番号は7桁の半角数字で入力してください。',
                                },
                              ]}
                            >
                              <Input
                                placeholder="例: 1234567"
                                className="ant-col ant-col-9"
                                disabled={!isSbsRoles}
                              />
                            </FormItem>
                          </Col>
                          <Col className="col-zipcode-button">
                            <Button
                              onClick={() => {
                                handleShowAddress(
                                  form,
                                  getFieldValue([
                                    'invoice_managements',
                                    field?.name,
                                    'zipcode',
                                  ]),
                                  [
                                    'invoice_managements',
                                    field.name,
                                    'zipcode',
                                  ],
                                  [
                                    'invoice_managements',
                                    field.name,
                                    'address',
                                  ],
                                );
                              }}
                              disabled={
                                checkInvalidZipcode(
                                  getFieldValue([
                                    'invoice_managements',
                                    field?.name,
                                    'zipcode',
                                  ]),
                                ) || !isSbsRoles
                              }
                            >
                              住所検索
                            </Button>
                          </Col>
                        </Row>
                        <FormItem
                          validateFirst
                          isHiddenDivider
                          name={[field.name, 'address']}
                          messageVariables={{ label: '住所' }}
                          rules={[
                            { required: true, whitespace: true },
                            {
                              max: 512,
                              transform(value) {
                                return transformEmojisIfHave(value);
                              },
                            },
                          ]}
                        >
                          <Input
                            placeholder="住所を入力してください"
                            onChange={() =>
                              handleChangeAddress(form, 'zipcode')
                            }
                            disabled={!isSbsRoles}
                          />
                        </FormItem>
                      </>
                    )}
                  </FormItem>
                  <FormItem
                    validateFirst
                    label="担当者名"
                    name={[field.name, 'contact_name']}
                    rules={[
                      { required: true, whitespace: true },
                      {
                        max: 512,
                        transform(value) {
                          return transformEmojisIfHave(value);
                        },
                      },
                      {
                        pattern: CONTACT_NAME_PATTERN,
                        message:
                          '全角カタカナ、ひらがな、漢字、アルファベットのみで入力してください。',
                      },
                    ]}
                    {...FORM_ITEM_LAYOUT_COMMON}
                  >
                    <Input
                      placeholder="フルネームを入力してください"
                      disabled={!isSbsRoles}
                    />
                  </FormItem>
                  <FormItem
                    validateFirst
                    label="担当者メールアドレス"
                    name={[field.name, 'email_address']}
                    rules={[
                      { required: true, whitespace: true },
                      {
                        max: 128,
                        transform(value) {
                          return transformEmojisIfHave(value);
                        },
                      },
                      {
                        pattern: EMAIL_PATTERN,
                        message: 'メールアドレスの形式で入力してください。',
                      },
                    ]}
                    {...FORM_ITEM_LAYOUT_COMMON}
                  >
                    <Input disabled={!isSbsRoles} />
                  </FormItem>
                  <FormItem
                    label="担当者電話番号"
                    required
                    name={[field.name, 'phone_number']}
                    validateFirst
                    rules={[
                      { required: true, whitespace: true },
                      {
                        pattern: NUMBER_PATTERN,
                        message: '電話番号は半角数字で入力してください。',
                      },
                      {
                        pattern: PHONE_NUMBER_LENGTH_PATTERN,
                        message:
                          '電話番号の桁数が不正です。正しい番号を入力してください。',
                      },
                    ]}
                    {...FORM_ITEM_LAYOUT_COMMON}
                  >
                    <Input
                      placeholder="ハイフンを入れずに入力してください"
                      disabled={!isSbsRoles}
                    />
                  </FormItem>
                  {isSbsRoles && (
                    <>
                      <FormItem
                        label="Aコード"
                        name={[field.name, 'acode']}
                        rules={[
                          ...(isInvoiceTypeReportForEachStore
                            ? invoiceManagementsTypeStore?.[field.name]?.acode
                              ? [{ required: true }]
                              : []
                            : invoiceManagementsTypeChain?.[field.name]?.acode
                              ? [{ required: true }]
                              : []),
                          { whitespace: true },
                          {
                            max: 512,
                            transform(value) {
                              return transformEmojisIfHave(value);
                            },
                          },
                        ]}
                        {...FORM_ITEM_LAYOUT_COMMON}
                      >
                        <Input placeholder="英数字を入力してください" />
                      </FormItem>
                      <FormItem
                        label="受取人口座"
                        name={[field.name, 'beneficiary_account']}
                        rules={[
                          ...(isInvoiceTypeReportForEachStore
                            ? invoiceManagementsTypeStore?.[field.name]
                                ?.beneficiary_account
                              ? [{ required: true }]
                              : []
                            : invoiceManagementsTypeChain?.[field.name]
                                  ?.beneficiary_account
                              ? [{ required: true }]
                              : []),
                          { whitespace: true },
                          {
                            max: 512,
                            transform(value) {
                              return transformEmojisIfHave(value);
                            },
                          },
                        ]}
                        {...FORM_ITEM_LAYOUT_COMMON}
                      >
                        <Input placeholder="数字を入力してください" />
                      </FormItem>
                    </>
                  )}
                </Card>
              ))
            }
          </Form.List>
        </>
      )}
      <SectionTitle title="インボイス制度" className="mt-32" />
      <Card variant="borderless">
        <FormItem
          label="インボイス制度の登録"
          name="invoice_registration"
          textPreview={
            invoice_registration &&
            (invoice_registration === '有' ? '有り' : '無し')
          }
          isPreviewMode
          isLastFormItem
          {...FORM_ITEM_LAYOUT_COMMON}
        />
      </Card>
      <Flex className="mt-32" justify="center">
        <SubmitButton form={form}>更新</SubmitButton>
      </Flex>
    </Form>
  );
}
